<template>
  <div
    class="deal-owner-picker"
    :class="{ 'deal-owner-picker--labeled': showLabel }"
    v-click-outside="closeMenu"
    @mousedown.stop
    @click.stop
  >
    <button
      ref="trigger"
      type="button"
      class="deal-owner-picker__trigger"
      :title="ownerFullName || $t('deals.assign_owner')"
      :aria-expanded="menuOpen"
      :aria-label="$t('deals.assign_owner')"
      @click.stop="toggleMenu"
    >
      <span
        class="deal-owner-picker__mark"
        :class="{ 'deal-owner-picker__mark--empty': !ownerInitials }"
      >
        <template v-if="ownerInitials">{{ ownerInitials }}</template>
        <img v-else src="/icons/pipeline-user-add.svg" width="12" height="12" alt="" />
      </span>
      <template v-if="showLabel">
        <span
          class="deal-owner-picker__label"
          :class="{ 'deal-owner-picker__label--empty': !ownerFullName }"
        >
          {{ label || ownerFullName || $t('deals.assign') }}
        </span>
        <BaseIcon name="chevron" size="12" :rotate="180" class="deal-owner-picker__chevron" />
      </template>
    </button>
    <Teleport to="body">
      <div v-if="menuOpen" class="deal-owner-menu" :style="menuStyle" @mousedown.stop>
        <button
          v-for="(choice, index) in choices"
          :key="choice.id"
          type="button"
          class="deal-owner-menu__item"
          :class="{ 'deal-owner-menu__item--before-divider': index === choices.length - 1 }"
          @click.stop="select(choice.id)"
        >
          <span class="deal-owner-menu__person">
            <span
              class="deal-owner-menu__avatar"
              :style="{ backgroundColor: choice.background, color: choice.color }"
            >
              {{ choice.initials }}
            </span>
            <span class="deal-owner-menu__name">{{ choice.label }}</span>
          </span>
          <img
            v-if="choice.id === ownerId"
            src="/icons/pipeline-owner-check.svg"
            width="14"
            height="14"
            alt=""
          />
        </button>
        <div class="deal-owner-menu__divider"></div>
        <button
          type="button"
          class="deal-owner-menu__item deal-owner-menu__item--after-divider"
          @click.stop="select(null)"
        >
          <span class="deal-owner-menu__person">
            <span class="deal-owner-menu__empty">
              <img src="/icons/pipeline-user-add.svg" width="12" height="12" alt="" />
            </span>
            <span class="deal-owner-menu__name">{{ $t('deals.unassigned') }}</span>
          </span>
          <img
            v-if="!ownerId"
            src="/icons/pipeline-owner-check.svg"
            width="14"
            height="14"
            alt=""
          />
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script>
  import { computed, ref, nextTick, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  const AVATAR_PALETTE = [
    { background: '#dbeafe', color: '#1d4ed8' },
    { background: '#ffedd5', color: '#c2410c' },
    { background: '#dcfce7', color: '#15803d' },
  ];

  export default {
    name: 'DealOwnerPicker',

    components: { BaseIcon },

    props: {
      owner: {
        type: Object,
        default: null,
      },
      ownerId: {
        type: String,
        default: null,
      },
      members: {
        type: Array,
        default: () => [],
      },
      // Render the owner name (or "assign" prompt) and a chevron next to the avatar
      showLabel: {
        type: Boolean,
        default: false,
      },
      // Overrides the owner name shown when showLabel is set
      label: {
        type: String,
        default: '',
      },
    },

    emits: ['select'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const menuOpen = ref(false);
      const trigger = ref(null);
      const menuStyle = ref({ top: '0px', left: '0px' });

      const ownerInitials = computed(() => {
        if (!props.owner) return '';
        const first = props.owner.first_name || '';
        const last = props.owner.last_name || '';
        return (first.charAt(0) + last.charAt(0)).toUpperCase();
      });

      const ownerFullName = computed(() => {
        if (!props.owner) return '';
        return [props.owner.first_name, props.owner.last_name].filter(Boolean).join(' ');
      });

      const choices = computed(() =>
        props.members
          .filter((member) => member.role !== 'installer')
          .map((member, index) => {
            const user = member.user || member;
            const id = user?.id ?? member.user_id;
            const first = user?.first_name || '';
            const last = user?.last_name || '';
            const email = user?.email || member.email || '';
            const palette = AVATAR_PALETTE[index % AVATAR_PALETTE.length];
            return {
              id,
              label: [first, last].filter(Boolean).join(' ').trim() || email,
              initials:
                (first.charAt(0) + last.charAt(0)).toUpperCase() ||
                (email || '?').charAt(0).toUpperCase(),
              background: palette.background,
              color: palette.color,
            };
          })
          .filter((choice) => choice.id)
      );

      const updateMenuPosition = () => {
        if (!trigger.value) return;
        const rect = trigger.value.getBoundingClientRect();
        const menuWidth = 208;
        const menuHeight = 12 + (choices.value.length + 1) * 36 + 9;
        let top = rect.bottom + 4;
        if (top + menuHeight > window.innerHeight - 8) {
          top = Math.max(8, rect.top - menuHeight - 4);
        }
        let left = rect.left;
        if (left + menuWidth > window.innerWidth - 8) {
          left = Math.max(8, window.innerWidth - menuWidth - 8);
        }
        menuStyle.value = { top: `${top}px`, left: `${left}px` };
      };

      const closeMenu = () => {
        menuOpen.value = false;
      };

      const toggleMenu = async () => {
        menuOpen.value = !menuOpen.value;
        if (!menuOpen.value) return;
        await nextTick();
        updateMenuPosition();
      };

      const select = (ownerId) => {
        closeMenu();
        if ((props.ownerId || null) === ownerId) return;
        emit('select', ownerId);
      };

      const closeOnScroll = () => {
        if (menuOpen.value) closeMenu();
      };

      onMounted(() => {
        window.addEventListener('scroll', closeOnScroll, true);
        window.addEventListener('resize', closeOnScroll);
      });

      onBeforeUnmount(() => {
        window.removeEventListener('scroll', closeOnScroll, true);
        window.removeEventListener('resize', closeOnScroll);
      });

      return {
        $t,
        menuOpen,
        trigger,
        menuStyle,
        ownerInitials,
        ownerFullName,
        choices,
        closeMenu,
        toggleMenu,
        select,
      };
    },
  };
</script>

<style scoped lang="scss">
  .deal-owner-picker {
    position: relative;
    flex-shrink: 0;

    &__trigger {
      display: inline-flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      margin: -4px;
      padding: 0;
      border: 0;
      border-radius: 4px;
      background: transparent;
      cursor: pointer;

      @include hover {
        background: var(--slate-100);
      }

      &[aria-expanded='true'] {
        background: var(--slate-100);
      }

      &:focus-visible {
        outline: 2px solid #3364e6;
        outline-offset: 2px;
      }
    }

    &__mark {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #e6e9ff;
      color: #3364e6;
      font-size: 9px;
      font-weight: 500;
      line-height: 1;

      &--empty {
        background: transparent;
        border: 1px dashed #d7d7d7;
      }

      img {
        display: block;
      }
    }

    &--labeled &__trigger {
      width: auto;
      height: 24px;
      margin: -2px -4px;
      padding: 2px 4px;
      gap: 6px;
    }

    &__label {
      color: var(--slate-900);
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
      white-space: nowrap;

      &--empty {
        color: var(--brand-600);
      }
    }

    &__chevron {
      color: var(--slate-500);
    }
  }

  .deal-owner-menu {
    position: fixed;
    z-index: 1200;
    display: flex;
    flex-direction: column;
    width: 208px;
    padding: 6px;
    background: #fff;
    border: 1px solid #d7d7d7;
    border-radius: 4px;
    box-shadow: 0 8px 12px rgba(0, 9, 33, 0.14);

    &__item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      width: 100%;
      padding: 8px;
      border: 0;
      border-radius: 4px;
      background: transparent;
      color: #000921;
      font-size: 13px;
      font-weight: 400;
      line-height: 20px;
      text-align: left;
      cursor: pointer;

      &--before-divider {
        padding-bottom: 12px;
      }

      &--after-divider {
        padding-top: 12px;
      }

      @include hover {
        background: #e6e9ff;
      }

      &:focus-visible {
        outline: 2px solid #3364e6;
        outline-offset: -2px;
      }
    }

    &__person {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }

    &__name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__avatar,
    &__empty {
      display: inline-flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
    }

    &__avatar {
      font-size: 9px;
      font-weight: 500;
      line-height: 1;
    }

    &__empty {
      background: transparent;
      border: 1px dashed #d7d7d7;

      img {
        display: block;
      }
    }

    &__divider {
      height: 1px;
      background: #e3e6ee;
    }
  }
</style>
